import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    CreditCard,
    AlertTriangle,
    Settings,
    FileText,
    Menu,
    X,
    LogOut,
    User,
    Shield,
    Users,
    Activity,
    Sparkles,
    Crown,
    Brain,
    Code,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useLogout } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);
    const logoutMutation = useLogout();
    const { data: organization } = useOrganization();

    const navigation = [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Payments', href: '/payments', icon: CreditCard },
        { name: 'Risks', href: '/risks', icon: AlertTriangle },
        { name: 'AI Patterns', href: '/intelligence/patterns', icon: Brain },
        { name: 'Reports', href: '/reports', icon: FileText },
        { name: 'Team', href: '/team', icon: Users },
        { name: 'Audit Logs', href: '/audit-logs', icon: Activity },
        { name: 'Pricing', href: '/pricing', icon: Crown },
        {
            name: 'Settings',
            href: '/settings',
            icon: Settings,
            children: [
                { name: 'Profile', href: '/settings/profile', icon: User },
                { name: 'Organization', href: '/settings/organization', icon: Settings },
                { name: 'Providers', href: '/settings/providers', icon: CreditCard },
                { name: 'API Keys', href: '/settings/api-keys', icon: Shield },
                { name: 'AI Settings', href: '/settings/ai', icon: Sparkles },
            ],
        },
    ];

    const isActive = (path: string) => location.pathname.startsWith(path);

    const handleLogout = () => {
        logoutMutation.mutate();
        navigate('/login');
    };

    // Get plan badge color based on plan type
    const getPlanBadgeColor = (plan: string) => {
        switch (plan.toLowerCase()) {
            case 'free':
                return 'bg-gray-100 text-gray-700';
            case 'basic':
                return 'bg-blue-100 text-blue-700';
            case 'standard':
                return 'bg-green-100 text-green-700';
            case 'pro':
                return 'bg-purple-100 text-purple-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-200">
                        <Shield className="w-8 h-8 text-primary-600" />
                        <div>
                            <h1 className="text-lg font-bold text-gray-900">silentloss</h1>
                            <p className="text-xs text-gray-500">Payment Safety</p>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                        {navigation.map((item) => {
                            const Icon = item.icon;
                            const hasChildren = 'children' in item && item.children;

                            if (hasChildren) {
                                const isExpanded = expandedMenu === item.name;
                                const isAnyChildActive = item.children.some((child: any) =>
                                    location.pathname.startsWith(child.href)
                                );

                                return (
                                    <div key={item.name}>
                                        <button
                                            onClick={() =>
                                                setExpandedMenu(isExpanded ? null : item.name)
                                            }
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isAnyChildActive
                                                ? 'bg-primary-50 text-primary-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                        >
                                            <Icon className="w-5 h-5" />
                                            <span className="flex-1 text-left">{item.name}</span>
                                            <svg
                                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''
                                                    }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </button>
                                        {isExpanded && (
                                            <div className="ml-8 mt-1 space-y-1">
                                                {item.children.map((child: any) => {
                                                    const ChildIcon = child.icon;
                                                    return (
                                                        <Link
                                                            key={child.name}
                                                            to={child.href}
                                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${isActive(child.href)
                                                                ? 'text-primary-700 font-medium'
                                                                : 'text-gray-600 hover:text-gray-900'
                                                                }`}
                                                            onClick={() => setSidebarOpen(false)}
                                                        >
                                                            <ChildIcon className="w-4 h-4" />
                                                            {child.name}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(item.href)
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    onClick={() => setSidebarOpen(false)}
                                >
                                    <Icon className="w-5 h-5" />
                                    {item.name}
                                </Link>
                            );
                        })}

                        {/* Developer Link - Opens in new tab */}
                        <a
                            href="http://developer.silentloss.io/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <Code className="w-5 h-5" />
                            Developer
                        </a>
                    </nav>

                    {/* User section */}
                    <div className="border-t border-gray-200 p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                                {user?.initials || user?.name?.substring(0, 2).toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user?.full_name || user?.display_name || user?.name || 'User'}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Top bar */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 lg:px-8">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
                        >
                            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                        <div className="flex-1 lg:ml-0 ml-4">
                            <h2 className="text-xl font-semibold text-gray-900">
                                {navigation.find((item) => isActive(item.href))?.name || 'Payment Safety'}
                            </h2>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* User Avatar - Link to Profile */}
                            {user && (
                                <Link
                                    to="/settings/profile"
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                    title="Profile Settings"
                                >
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                                        {user.initials || user.name?.substring(0, 2).toUpperCase() || 'U'}
                                    </div>
                                    <span className="hidden xl:inline-block text-sm font-medium text-gray-700">
                                        {user.display_name || user.first_name || user.name}
                                    </span>
                                </Link>
                            )}

                            {organization && (
                                <>
                                    {/* Organization Name Badge - Shows short on mobile, full on desktop */}
                                    <span className="hidden sm:inline-block md:hidden px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 uppercase">
                                        {organization.name.substring(0, 2)}
                                    </span>
                                    <span className="hidden md:inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                                        {organization.name}
                                    </span>

                                    {/* Plan Badge - Real Data from Organization */}
                                    <span
                                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(
                                            organization.plan?.code || organization.plan as any
                                        )}`}
                                    >
                                        {organization.plan?.name || organization.plan_label}
                                    </span>

                                    {/* Upgrade Button - Only for Free/Basic/Standard Users */}
                                    {organization.upgrade?.available && organization.plan?.code !== 'pro' && (
                                        <Link
                                            to="/pricing"
                                            className="hidden lg:inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 transition-all shadow-sm"
                                        >
                                            <Crown className="w-3 h-3" />
                                            Upgrade
                                        </Link>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Page content */}
                <main className="p-4 lg:p-8">{children}</main>
            </div>
        </div>
    );
};

export default Layout;
